import React, { useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Paper, Modal, Box, Typography, Checkbox, FormControlLabel,
  Divider, Switch, Stack, CircularProgress,
} from "@mui/material";
import WifiIcon from "@mui/icons-material/Wifi";
import { Link } from "react-router-dom";

import removeStudentFromClass from "./removeStudentFromClass";
import TLHClassesAPICalls from "services/TLHClassesAPICalls";
import * as CONSTANTS from "./../../../StringConstants.js"

const dayOrder = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

const convertTimeToNumber = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

const IndividualStudentProfileClassesTable = ({ tlhStudentData }) => {
  const [onlineModalClass, setOnlineModalClass] = useState(null); // classItem
  const [lessonRecords, setLessonRecords] = useState([]); // fetched from backend
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set()); // classLessonRecordIds
  const [allOnline, setAllOnline] = useState(false);
  const [saving, setSaving] = useState(false);

  const classesArray = tlhStudentData.classes ? Object.values(tlhStudentData.classes) : [];

  const sortedClasses = [...classesArray].sort((a, b) => {
    const dayDiff = dayOrder.indexOf(a.classDay) - dayOrder.indexOf(b.classDay);
    if (dayDiff !== 0) return dayDiff;
    return convertTimeToNumber(a.startTime) - convertTimeToNumber(b.startTime);
  });

  const handleRemoveClick = async (classId) => {
    try {
      await removeStudentFromClass(classId, tlhStudentData.tlhStudentId);
    } catch (error) {
      alert("Failed to remove student. Please try again.");
    }
  };

  const openOnlineModal = async (classItem) => {
    setOnlineModalClass(classItem);
    setSelectedIds(new Set());
    setAllOnline(false);
    setLessonRecords([]);
    setLoadingRecords(true);
    try {
      console.log('[OnlineModal] Fetching lesson records for studentId:', tlhStudentData.tlhStudentId, 'classId:', classItem.classId);
      const res = await TLHClassesAPICalls.getStudentLessonRecordsForClass(tlhStudentData.tlhStudentId, classItem.classId);
      console.log('[OnlineModal] getStudentLessonRecordsForClass response:', res.data);

      const records = res.data || [];
      console.log('[OnlineModal] mapped records:', records);
      setLessonRecords(records);

      const onlineIds = new Set(records.filter(r => r.isOnline).map(r => r.classLessonRecordId));
      setSelectedIds(onlineIds);
      setAllOnline(onlineIds.size === records.length && records.length > 0);
    } catch (err) {
      console.error('[OnlineModal] Failed to fetch lesson records:', err);
      console.error('[OnlineModal] Response status:', err.response?.status);
      console.error('[OnlineModal] Response body:', err.response?.data);
    } finally {
      setLoadingRecords(false);
    }
  };

  const closeOnlineModal = () => {
    setOnlineModalClass(null);
    setLessonRecords([]);
    setSelectedIds(new Set());
    setAllOnline(false);
  };

  const toggleRecord = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAllOnlineToggle = (checked) => {
    setAllOnline(checked);
    if (checked) {
      setSelectedIds(new Set(lessonRecords.map(r => r.classLessonRecordId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 3, boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1976d2" }}>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Class Name</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Start Time</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Class Day</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Class Type</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedClasses.length > 0 ? (
              sortedClasses.map((classItem, index) => {
                const previousClass = sortedClasses[index - 1];
                const showGap = previousClass && previousClass.classDay !== classItem.classDay;

                return (
                  <React.Fragment key={classItem.classId}>
                    {showGap && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ backgroundColor: "#f0f0f0", height: "15px" }} />
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>
                        <Link
                          to={CONSTANTS.ViewTLHClass + "/" + classItem.classId}
                          style={{ textDecoration: "none", color: "#1976d2", fontWeight: "bold" }}
                        >
                          {classItem.className}
                        </Link>
                      </TableCell>
                      <TableCell>{classItem.startTime}</TableCell>
                      <TableCell>{classItem.classDay}</TableCell>
                      <TableCell>{classItem.classType.replace(/_/g, " ")}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<WifiIcon />}
                            onClick={() => openOnlineModal(classItem)}
                          >
                            Online
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => handleRemoveClick(classItem.classId)}
                          >
                            Remove
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No classes found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Online lessons modal */}
      <Modal open={onlineModalClass !== null} onClose={closeOnlineModal}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper", borderRadius: 2,
          boxShadow: 24, p: 4, width: 420,
        }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Set Online Lessons
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {onlineModalClass?.className} — select which upcoming lessons this student will attend online.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={allOnline}
                onChange={(e) => handleAllOnlineToggle(e.target.checked)}
              />
            }
            label="Set all upcoming lessons as online"
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
            Next 6 upcoming lessons
          </Typography>

          {loadingRecords ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : lessonRecords.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No upcoming lessons found.
            </Typography>
          ) : (
            lessonRecords.map((record) => (
              <FormControlLabel
                key={record.classLessonRecordId}
                control={
                  <Checkbox
                    checked={selectedIds.has(record.classLessonRecordId)}
                    onChange={() => toggleRecord(record.classLessonRecordId)}
                  />
                }
                label={`${formatDate(record.lessonDate)}${record.startTime ? `  •  ${record.startTime.slice(0, 5)}` : ""}`}
                sx={{ display: "flex", ml: 0.5 }}
              />
            ))
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button onClick={closeOnlineModal} disabled={saving}>Cancel</Button>
            <Button
              variant="contained"
              disabled={saving || loadingRecords}
              onClick={async () => {
                setSaving(true);
                try {
                  await TLHClassesAPICalls.updateStudentOnlineStatus(
                    tlhStudentData.tlhStudentId,
                    onlineModalClass.classId,
                    true,
                    allOnline,
                    allOnline ? [] : [...selectedIds],
                  );
                  closeOnlineModal();
                } catch (err) {
                  console.error("Failed to update online status:", err);
                  alert("Failed to save. Please try again.");
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default IndividualStudentProfileClassesTable;
